import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CheckIcon } from 'lucide-react';
import { Button } from '../ui/Button';
const steps = ['Personal information', 'Volunteer interest', 'Availability', 'Skills', 'Consent'];
const interests = ['Ward canvassing', 'Digital campaign', 'Community mobilisation', 'Events support', 'Research & policy', 'Media & creative', 'Technology', 'Professional services'];
const availabilityOptions = ['Weekdays', 'Weekends', 'Evenings', 'Remote only', 'In person'];
const skillOptions = ['Legal', 'Medical', 'Data & analytics', 'Design', 'Photography', 'Translation', 'Logistics', 'Teaching', 'Security', 'Accounting'];
const nigerianStates = ['Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'];
type Errors = Record<string, string>;
export function VolunteerForm() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [state, setState] = useState('');
  const [lga, setLga] = useState('');
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [availability, setAvailability] = useState<string[]>([]);
  const [skills, setSkills] = useState<string[]>([]);
  const [contactConsent, setContactConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const toggle = (value: string, list: string[], setter: (next: string[]) => void) => {
    setter(list.includes(value) ? list.filter(item => item !== value) : [...list, value]);
  };
  const validateStep = (): Errors => {
    const next: Errors = {};
    if (step === 0) {
      if (!firstName.trim()) next.firstName = 'Enter your first name.';
      if (!lastName.trim()) next.lastName = 'Enter your last name.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Enter a valid email address.';
      if (phone.replace(/\D/g, '').length < 10) next.phone = 'Enter a valid phone number.';
      if (!state) next.state = 'Select your state.';
      if (!lga.trim()) next.lga = 'Enter your local government area.';
    }
    if (step === 1 && selectedInterests.length === 0) {
      next.interests = 'Select at least one area.';
    }
    if (step === 2 && availability.length === 0) {
      next.availability = 'Select at least one option.';
    }
    if (step === 4) {
      if (!contactConsent) next.contactConsent = 'Consent is required to contact you.';
      if (!privacyConsent) next.privacyConsent = 'Please acknowledge the privacy notice.';
    }
    return next;
  };
  const goNext = () => {
    const found = validateStep();
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    if (step < steps.length - 1) {
      setStep(step + 1);
      return;
    }
    setSubmitting(true);
    window.setTimeout(() => {
      setSubmitting(false);
      setComplete(true);
    }, 900);
  };
  const inputClass = (hasError: boolean) => `w-full rounded-lg border bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-500/70 transition-colors duration-150 ease-emphasis focus:outline-none ${hasError ? 'border-red-600' : 'border-sand-300 focus:border-green-600'}`;
  const chipClass = (active: boolean) => `rounded-lg border px-4 py-2.5 text-[14px] font-medium transition-colors duration-150 ease-emphasis ${active ? 'border-green-600 bg-green-50 text-green-800' : 'border-sand-300 bg-white text-slate-600 hover:border-slate-500/50 hover:text-ink'}`;
  if (complete) {
    return <div role="status" className="rounded-media border border-green-200 bg-white p-8 text-center lg:p-12" data-id="element-32" data-mp="25pc4pb18yroo">
        <CheckCircle2Icon aria-hidden className="mx-auto h-10 w-10 text-green-600" data-id="element-33" data-mp="2ei5wu91slljy" />
        <h2 className="mt-5 font-display text-[28px] leading-tight text-ink" data-id="element-34" data-mp="2ek8mut2y3eoo">
          Thank you for joining the movement, {firstName}.
        </h2>
        <p className="mx-auto mt-3 max-w-lg text-[16px] leading-relaxed text-slate-600" data-id="element-35" data-mp="1ms1fykd8rgnh">
          Your ward coordinator in {lga}, {state} will contact you within five working days with
          your first assignment and training date.
        </p>
        <p className="mt-6 inline-block rounded-lg bg-sand-100 px-5 py-3 text-[15px]" data-id="element-36" data-mp="3c5o3nebw8dlb">
          <span className="text-slate-500" data-id="element-37" data-mp="3c7r0ah7jx4z0">Volunteer reference</span>{' '}
          <span className="font-semibold text-ink" data-id="element-38" data-mp="3761ov0gf2pae">
            ASB-{state.slice(0, 2).toUpperCase()}-{String(Math.floor(Math.random() * 9000) + 1000)}
          </span>
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3" data-id="element-39" data-mp="xyy92s0rvjcm">
          <Link to="/events" className="inline-flex h-12 items-center rounded-lg bg-green-600 px-6 text-[15px] font-semibold text-white transition-colors duration-150 ease-emphasis hover:bg-green-700" data-id="element-40" data-mp="jydt03m8s1mt">
            See upcoming events
          </Link>
          <Link to="/resources" className="inline-flex h-12 items-center rounded-lg border border-ink/20 px-6 text-[15px] font-semibold text-ink transition-colors duration-150 ease-emphasis hover:border-ink/40 hover:bg-ink/[0.04]" data-id="element-41" data-mp="1godawooa8ci2">
            Download campaign materials
          </Link>
        </div>
      </div>;
  }
  return <div className="rounded-media border border-sand-200 bg-white p-6 sm:p-9" data-id="element-42" data-mp="1vkcgnsnx60k1">
      <ol className="flex flex-wrap gap-x-6 gap-y-2" aria-label="Application progress" data-id="element-43" data-mp="20d5lshnavawn">
        {steps.map((label, index) => {
        const done = index < step;
        const current = index === step;
        return <li key={label} className="flex items-center gap-2 text-[13px]" data-id="element-44" data-mp="2576nbleu5e2q" data-mpi={index}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-bold ${done ? 'bg-green-600 text-white' : current ? 'bg-ink text-white' : 'bg-sand-200 text-slate-600'}`} data-id="element-45" data-mp="208vgzknr1lp9">
                {done ? <CheckIcon aria-hidden className="h-3.5 w-3.5" data-id="element-46" data-mp="1hivsozzqgamw" /> : index + 1}
              </span>
              <span className={current ? 'font-semibold text-ink' : 'text-slate-500'} data-id="element-47" data-mp="1cl3z81jfwggl">
                {label}
              </span>
            </li>;
      })}
      </ol>

      <div className="mt-8 border-t border-sand-200 pt-8" data-id="element-48" data-mp="13jw7k2bfidvh">
        {step === 0 && <fieldset data-id="element-49" data-mp="13m9z416wh7ya">
            <legend className="font-display text-[24px] text-ink" data-id="element-50" data-mp="3f7skvgpks7p2">Personal information</legend>
            <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-51" data-mp="3j8kvoee3ikng">
              <div data-id="element-52" data-mp="3j71hctc02job">
                <label htmlFor="v-first" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-53" data-mp="3o7xtfu6i9c4t">
                  First name <span className="text-red-600" data-id="element-54" data-mp="3t6saaqkwwqma">*</span>
                </label>
                <input id="v-first" autoComplete="given-name" value={firstName} onChange={event => setFirstName(event.target.value)} aria-invalid={Boolean(errors.firstName)} className={inputClass(Boolean(errors.firstName))} data-id="element-55" data-mp="3t4o94geee5pp" />
                {errors.firstName && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-56" data-mp="3aa986l8j02z8">{errors.firstName}</p>}
              </div>
              <div data-id="element-57" data-mp="2hrlrrj4h5c4h">
                <label htmlFor="v-last" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-58" data-mp="2hr3nbz7nrrlh">
                  Last name <span className="text-red-600" data-id="element-59" data-mp="1b5kqdcdvjb39">*</span>
                </label>
                <input id="v-last" autoComplete="family-name" value={lastName} onChange={event => setLastName(event.target.value)} aria-invalid={Boolean(errors.lastName)} className={inputClass(Boolean(errors.lastName))} data-id="element-60" data-mp="165zpuxj22uof" />
                {errors.lastName && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-61" data-mp="1p4kdgflqnhxb">{errors.lastName}</p>}
              </div>
              <div data-id="element-62" data-mp="22vyzkvaxzz8l">
                <label htmlFor="v-email" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-63" data-mp="22ufjb4pado6t">
                  Email <span className="text-red-600" data-id="element-64" data-mp="1yuni5d282kbh">*</span>
                </label>
                <input id="v-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} aria-invalid={Boolean(errors.email)} className={inputClass(Boolean(errors.email))} data-id="element-65" data-mp="2cuy7gbnvvquj" />
                {errors.email && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-66" data-mp="2hhwaw7dxfgyq">{errors.email}</p>}
              </div>
              <div data-id="element-67" data-mp="2mfxsenvpf883">
                <label htmlFor="v-phone" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-68" data-mp="2mjlmpp8z3ynv">
                  Phone <span className="text-red-600" data-id="element-69" data-mp="28o9i8q50mttr">*</span>
                </label>
                <input id="v-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+234 800 000 0000" value={phone} onChange={event => setPhone(event.target.value)} aria-invalid={Boolean(errors.phone)} className={inputClass(Boolean(errors.phone))} data-id="element-70" data-mp="2di5d3jpzg0kp" />
                {errors.phone && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-71" data-mp="11pgn5ffwt1dm">{errors.phone}</p>}
              </div>
              <div data-id="element-72" data-mp="16p30dovqmhmf">
                <label htmlFor="v-state" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-73" data-mp="16pm687845fk3">
                  State <span className="text-red-600" data-id="element-74" data-mp="sqmj540918q1">*</span>
                </label>
                <select id="v-state" value={state} onChange={event => setState(event.target.value)} aria-invalid={Boolean(errors.state)} className={inputClass(Boolean(errors.state))} data-id="element-75" data-mp="wrol3sljvyvp">
                  <option value="" data-id="element-76" data-mp="1bopctw3qrtu6">Select your state</option>
                  {nigerianStates.map(item => <option key={item} value={item} data-id="element-77" data-mp="1fo6l0xajz531">
                      {item}
                    </option>)}
                </select>
                {errors.state && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-78" data-mp="286bxeglpadfb">{errors.state}</p>}
              </div>
              <div data-id="element-79" data-mp="3svpbx9hb02f7">
                <label htmlFor="v-lga" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-80" data-mp="3sv5ya4yos06z">
                  Local government area <span className="text-red-600" data-id="element-81" data-mp="3nwl34jyak6pe">*</span>
                </label>
                <input id="v-lga" value={lga} onChange={event => setLga(event.target.value)} aria-invalid={Boolean(errors.lga)} className={inputClass(Boolean(errors.lga))} data-id="element-82" data-mp="6fl821kvfuhq" />
                {errors.lga && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-83" data-mp="34zbl978m71a9">{errors.lga}</p>}
              </div>
            </div>
          </fieldset>}

        {step === 1 && <fieldset data-id="element-84" data-mp="3evwrtx0a8kg7">
            <legend className="font-display text-[24px] text-ink" data-id="element-85" data-mp="3eveko5c30lti">Where would you like to help?</legend>
            <p className="mt-2 text-[15px] text-slate-600" data-id="element-86" data-mp="z48sjuxsqfu8">Select as many as apply.</p>
            <div className="mt-6 flex flex-wrap gap-2.5" data-id="element-87" data-mp="u70arvgk8dti">
              {interests.map(item => <button key={item} type="button" aria-pressed={selectedInterests.includes(item)} onClick={() => toggle(item, selectedInterests, setSelectedInterests)} className={chipClass(selectedInterests.includes(item))} data-id="element-88" data-mp="118dm53t727tc">
                  {item}
                </button>)}
            </div>
            {errors.interests && <p className="mt-3 text-[13px] text-red-600" data-id="element-89" data-mp="ih2x6s714543">{errors.interests}</p>}
          </fieldset>}

        {step === 2 && <fieldset data-id="element-90" data-mp="sg27sgbcuatq">
            <legend className="font-display text-[24px] text-ink" data-id="element-91" data-mp="sfisz8h042jx">When are you available?</legend>
            <p className="mt-2 text-[15px] text-slate-600" data-id="element-92" data-mp="x9d61t23pja1">
              Two hours a week is enough to be useful.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5" data-id="element-93" data-mp="3vvzicqdfhksl">
              {availabilityOptions.map(item => <button key={item} type="button" aria-pressed={availability.includes(item)} onClick={() => toggle(item, availability, setAvailability)} className={chipClass(availability.includes(item))} data-id="element-94" data-mp="2i1isfkeq07vz">
                  {item}
                </button>)}
            </div>
            {errors.availability && <p className="mt-3 text-[13px] text-red-600" data-id="element-95" data-mp="1z3yc51q0uhmz">{errors.availability}</p>}
          </fieldset>}

        {step === 3 && <fieldset data-id="element-96" data-mp="1qameeggxbx74">
            <legend className="font-display text-[24px] text-ink" data-id="element-97" data-mp="1qa39q1gab5b1">Any professional skills?</legend>
            <p className="mt-2 text-[15px] text-slate-600" data-id="element-98" data-mp="1lc1ob9vdytwe">Optional — but it helps us place you well.</p>
            <div className="mt-6 flex flex-wrap gap-2.5" data-id="element-99" data-mp="1gcorpxepq8gm">
              {skillOptions.map(item => <button key={item} type="button" aria-pressed={skills.includes(item)} onClick={() => toggle(item, skills, setSkills)} className={chipClass(skills.includes(item))} data-id="element-100" data-mp="1bd2f9q6pkw8v">
                  {item}
                </button>)}
            </div>
          </fieldset>}

        {step === 4 && <fieldset data-id="element-101" data-mp="36naiu8j5q29p">
            <legend className="font-display text-[24px] text-ink" data-id="element-102" data-mp="36msawen4tzly">Consent</legend>
            <div className="mt-6 space-y-5" data-id="element-103" data-mp="2snsnfabcvqrd">
              <label className="flex items-start gap-3" data-id="element-104" data-mp="2xnx8ldn7l9nx">
                <input type="checkbox" checked={contactConsent} onChange={event => setContactConsent(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[#056839]" data-id="element-105" data-mp="2xpi1kytvkkjo" />
                <span className="text-[15px] leading-relaxed text-slate-700" data-id="element-106" data-mp="gzb81iownbuc">
                  I agree to be contacted by the campaign about volunteering, training and events.
                </span>
              </label>
              {errors.contactConsent && <p className="text-[13px] text-red-600" data-id="element-107" data-mp="21hlr7baeh1w1">{errors.contactConsent}</p>}
              <label className="flex items-start gap-3" data-id="element-108" data-mp="2g6hukv1ihqqb">
                <input type="checkbox" checked={privacyConsent} onChange={event => setPrivacyConsent(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[#056839]" data-id="element-109" data-mp="2g82pwmvnhdp8" />
                <span className="text-[15px] leading-relaxed text-slate-700" data-id="element-110" data-mp="2l647t4hcb6z4">
                  I have read the privacy notice and understand how my data will be stored and
                  used.
                </span>
              </label>
              {errors.privacyConsent && <p className="text-[13px] text-red-600" data-id="element-111" data-mp="14rba3i1ik25v">{errors.privacyConsent}</p>}
            </div>

            <div className="mt-8 rounded-card border border-sand-200 bg-sand-50 p-5" data-id="element-112" data-mp="zsrojiqu0se1">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-113" data-mp="2zur7rgkwejq8">
                Summary
              </h3>
              <dl className="mt-3 grid gap-2 text-[14px] sm:grid-cols-2" data-id="element-114" data-mp="34udlrs8jw403">
                <div data-id="element-115" data-mp="38uo0puebm4r3">
                  <dt className="inline text-slate-500" data-id="element-116" data-mp="38uecab8xc5ni">Name: </dt>
                  <dd className="inline text-ink" data-id="element-117" data-mp="3dus90x52rt68">
                    {firstName} {lastName}
                  </dd>
                </div>
                <div data-id="element-118" data-mp="3isu46gfrziy2">
                  <dt className="inline text-slate-500" data-id="element-119" data-mp="3ir97ol9whpxk">Location: </dt>
                  <dd className="inline text-ink" data-id="element-120" data-mp="3nr5bokillhd4">
                    {lga}, {state}
                  </dd>
                </div>
                <div className="sm:col-span-2" data-id="element-121" data-mp="3sl9k787ihxgb">
                  <dt className="inline text-slate-500" data-id="element-122" data-mp="fm8047chmpr">Interests: </dt>
                  <dd className="inline text-ink" data-id="element-123" data-mp="5epdfpr8luy9">{selectedInterests.join(', ') || '—'}</dd>
                </div>
                <div className="sm:col-span-2" data-id="element-124" data-mp="ad0ou4x6kdil">
                  <dt className="inline text-slate-500" data-id="element-125" data-mp="adtjw8ms1ekw">Availability: </dt>
                  <dd className="inline text-ink" data-id="element-126" data-mp="kia053464xp8">{availability.join(', ') || '—'}</dd>
                </div>
              </dl>
            </div>
          </fieldset>}
      </div>

      <div className="mt-8 flex items-center justify-between border-t border-sand-200 pt-6" data-id="element-127" data-mp="aij6f3nfmmq7">
        <Button variant="ghost" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="gap-2" data-id="element-128" data-mp="6ihdbivrj1mj">
          <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-129" data-mp="y8vlqzl0blqt" />
          Back
        </Button>
        <Button size="lg" onClick={goNext} loading={submitting} data-id="element-130" data-mp="yb81d63alk0p">
          {step === steps.length - 1 ? 'Submit application' : 'Continue'}
          {!submitting && <ArrowRightIcon aria-hidden className="h-[18px] w-[18px]" data-id="element-131" data-mp="pfihj1hhsxpf" />}
        </Button>
      </div>
    </div>;
}